import { Box, IconButton, Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { keyframes } from '@mui/material/styles'
import { ResponsiveLine, Serie } from '@nivo/line'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { getJson, getUrl } from '../providers/data'
import { UsageTimeseriesPoint } from '../types/apiRequests'
import { formatCompact, formatCurrency } from '../utils/formatNumber'
import { xxllncColor } from '../layout/theme/color'

type Period = 'daily' | 'monthly'

interface UsageChartProps {
  organizationId: string
  showCosts?: boolean
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const R = '16px'

const COLORS = {
  mcp: xxllncColor.dark100,
  workflow: xxllncColor.green100,
}

interface BuildResult {
  series: Serie[]
  mcpTotal: number
  workflowTotal: number
  mcpCostTotal: number
  workflowCostTotal: number
}

const buildSeries = (data: UsageTimeseriesPoint[], period: Period): BuildResult => {
  let mcpTotal = 0
  let workflowTotal = 0
  let mcpCostTotal = 0
  let workflowCostTotal = 0

  if (period === 'monthly') {
    const monthMap = new Map<string, { mcp: number; workflow: number }>()
    data.forEach((p) => {
      const key = p.date.substring(0, 7)
      const entry = monthMap.get(key) ?? { mcp: 0, workflow: 0 }
      entry.mcp += p.mcp
      entry.workflow += p.workflow ?? 0
      monthMap.set(key, entry)
      mcpTotal += p.mcp
      workflowTotal += p.workflow ?? 0
      mcpCostTotal += p.mcp_cost ?? 0
      workflowCostTotal += p.workflow_cost ?? 0
    })

    const keys = Array.from(monthMap.keys()).sort()
    const formatMonth = (key: string): string => {
      const [y, m] = key.split('-')
      const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1)
      return d.toLocaleDateString('nl-NL', { month: 'short', year: '2-digit' })
    }

    return {
      series: [
        { id: 'MCP', data: keys.map((k) => ({ x: formatMonth(k), y: monthMap.get(k)!.mcp })) },
        { id: 'Workflow', data: keys.map((k) => ({ x: formatMonth(k), y: monthMap.get(k)!.workflow })) },
      ],
      mcpTotal,
      workflowTotal,
      mcpCostTotal,
      workflowCostTotal,
    }
  }

  data.forEach((p) => {
    mcpTotal += p.mcp
    workflowTotal += p.workflow ?? 0
    mcpCostTotal += p.mcp_cost ?? 0
    workflowCostTotal += p.workflow_cost ?? 0
  })

  const formatDay = (dateStr: string): string => {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
  }

  return {
    series: [
      { id: 'MCP', data: data.map((p) => ({ x: formatDay(p.date), y: p.mcp })) },
      { id: 'Workflow', data: data.map((p) => ({ x: formatDay(p.date), y: p.workflow ?? 0 })) },
    ],
    mcpTotal,
    workflowTotal,
    mcpCostTotal,
    workflowCostTotal,
  }
}

const PAGE_SIZE = 15

export const UsageChart: FC<UsageChartProps> = ({ organizationId, showCosts = true }) => {
  const translate = useTranslate()
  const [period, setPeriod] = useState<Period>('daily')
  const [tablePage, setTablePage] = useState(0)
  const [data, setData] = useState<UsageTimeseriesPoint[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const dateFrom = useMemo(() => {
    if (period === 'daily') {
      const d = new Date()
      d.setDate(d.getDate() - 90)
      return d.toISOString().split('T')[0]
    }
    return undefined
  }, [period])

  useEffect(() => {
    if (!organizationId) return
    setIsLoading(true)
    setError(null)

    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)

    const qs = params.toString()
    const url = `${getUrl()}/usage_stats/timeseries/${organizationId}${qs ? `?${qs}` : ''}`
    getJson<UsageTimeseriesPoint[]>(url)
      .then(setData)
      .catch((err) => {
        console.error('Failed to load usage timeseries:', err)
        setData([])
        setError(translate('usageChart.loadError'))
      })
      .finally(() => setIsLoading(false))
  }, [organizationId, dateFrom, translate])

  const {
    series, mcpTotal, workflowTotal,
    mcpCostTotal, workflowCostTotal,
  } = useMemo(
    () => buildSeries(data, period),
    [data, period],
  )
  const totalCost = mcpCostTotal + workflowCostTotal

  const tableRows = useMemo(() => {
    if (period === 'monthly') {
      const monthMap = new Map<string, {
        mcp: number; workflow: number;
        mcpCost: number; workflowCost: number;
      }>()
      data.forEach((p) => {
        const key = p.date.substring(0, 7)
        const e = monthMap.get(key) ?? {
          mcp: 0, workflow: 0, mcpCost: 0, workflowCost: 0,
        }
        e.mcp += p.mcp
        e.workflow += p.workflow ?? 0
        e.mcpCost += p.mcp_cost ?? 0
        e.workflowCost += p.workflow_cost ?? 0
        monthMap.set(key, e)
      })
      return Array.from(monthMap.entries())
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([key, v]) => {
          const [y, m] = key.split('-')
          const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1)
          return {
            label: d.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' }),
            mcp: v.mcp, workflow: v.workflow,
            mcpCost: v.mcpCost, workflowCost: v.workflowCost,
          }
        })
    }
    return [...data]
      .sort((a, b) => b.date.localeCompare(a.date))
      .map((p) => {
        const d = new Date(p.date + 'T00:00:00')
        return {
          label: d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
          mcp: p.mcp, workflow: p.workflow ?? 0,
          mcpCost: p.mcp_cost ?? 0, workflowCost: p.workflow_cost ?? 0,
        }
      })
  }, [data, period])

  const totalPages = Math.ceil(tableRows.length / PAGE_SIZE)
  const pagedRows = tableRows.slice(tablePage * PAGE_SIZE, (tablePage + 1) * PAGE_SIZE)

  useEffect(() => { setTablePage(0) }, [period])

  if (isLoading) {
    return (
      <Box sx={{ background: '#fff', borderRadius: R, border: '1px solid rgba(0,0,0,0.06)', p: 3, mb: 2 }}>
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: '8px' }} />
      </Box>
    )
  }

  return (
    <Box sx={{
      background: '#fff', borderRadius: R, border: '1px solid rgba(0,0,0,0.06)',
      p: 3, mb: 2, animation: `${fadeIn} 0.4s ease-out both`,
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS.mcp }} />
            <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>MCP</Typography>
            <Typography sx={{
              fontSize: 18, fontWeight: 700, color: COLORS.mcp,
              fontVariantNumeric: 'tabular-nums', ml: 0.5,
            }}>
              {formatCompact(mcpTotal)}
            </Typography>
            {showCosts && mcpCostTotal > 0 && (
              <Typography sx={{ fontSize: 12, color: xxllncColor.green100, fontWeight: 600, ml: 0.5 }}>
                {formatCurrency(mcpCostTotal)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS.workflow }} />
            <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>Workflow</Typography>
            <Typography sx={{
              fontSize: 18, fontWeight: 700, color: COLORS.workflow,
              fontVariantNumeric: 'tabular-nums', ml: 0.5,
            }}>
              {formatCompact(workflowTotal)}
            </Typography>
            {showCosts && workflowCostTotal > 0 && (
              <Typography sx={{ fontSize: 12, color: xxllncColor.green100, fontWeight: 600, ml: 0.5 }}>
                {formatCurrency(workflowCostTotal)}
              </Typography>
            )}
          </Box>
          {showCosts && totalCost > 0 && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: xxllncColor.opGreen10, borderRadius: '8px', px: 1.5, py: 0.5,
            }}>
              <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>Totaal</Typography>
              <Typography sx={{
                fontSize: 16, fontWeight: 700, color: xxllncColor.green100,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {formatCurrency(totalCost)}
              </Typography>
            </Box>
          )}
        </Box>

        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, v: Period | null) => { if (v) setPeriod(v) }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              'textTransform': 'none',
              'fontWeight': 600,
              'fontSize': 11,
              'px': 1.5,
              'py': 0.25,
              'border': '1px solid rgba(0,0,0,0.08)',
              'color': 'rgba(0,0,0,0.4)',
              '&.Mui-selected': {
                'bgcolor': xxllncColor.opDark10,
                'color': xxllncColor.dark100,
                'borderColor': xxllncColor.opDark20,
                '&:hover': { bgcolor: xxllncColor.opDark20 },
              },
            },
          }}
        >
          <ToggleButton value="daily">{translate('usageChart.daily')}</ToggleButton>
          <ToggleButton value="monthly">{translate('usageChart.monthly')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ height: 280 }}>
        {error ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ fontSize: 13, color: xxllncColor.red100 }}>
              {error}
            </Typography>
          </Box>
        ) : series[0]?.data.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ fontSize: 13, color: 'rgba(0,0,0,0.25)' }}>
              {translate('usageChart.noData')}
            </Typography>
          </Box>
        ) : (
          <ResponsiveLine
            data={series}
            margin={{ top: 8, right: 12, bottom: 32, left: 44 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 0, stacked: false }}
            curve="monotoneX"
            enableArea={true}
            areaOpacity={0.06}
            colors={[COLORS.mcp, COLORS.workflow]}
            lineWidth={2}
            enablePoints={false}
            enableGridX={false}
            gridYValues={4}
            axisBottom={{
              tickSize: 0,
              tickPadding: 10,
              tickRotation: series[0]?.data.length > 15 ? -45 : 0,
              tickValues: series[0]?.data.length > 20
                ? series[0].data.filter((_, i) => i % Math.ceil(series[0].data.length / 15) === 0).map((d) => d.x as string)
                : undefined,
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 8,
              tickValues: 4,
            }}
            theme={{
              axis: {
                ticks: {
                  text: { fill: 'rgba(0,0,0,0.3)', fontSize: 10, fontWeight: 500 },
                },
              },
              grid: {
                line: { stroke: 'rgba(0,0,0,0.04)', strokeWidth: 1 },
              },
              crosshair: {
                line: { stroke: xxllncColor.opDark40, strokeWidth: 1, strokeDasharray: '4 4' },
              },
            }}
            useMesh={true}
            tooltip={({ point }) => {
              const seriesId = String(point.serieId)
              const color = seriesId === 'MCP'
                ? COLORS.mcp
                : seriesId === 'Workflow'
                  ? COLORS.workflow
                  : '#fff'
              const unit = seriesId === 'Workflow' ? 'executions' : 'calls'
              return (
                <Box sx={{
                  background: '#1a1a1a', color: '#fff', borderRadius: '8px',
                  px: 1.5, py: 0.75, fontSize: 12, fontWeight: 500,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}>
                  <span style={{ color, fontWeight: 700 }}>
                    {point.data.yFormatted}
                  </span>
                  {' '}
                  {seriesId.toLowerCase()} {unit}
                  <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 8 }}>{point.data.xFormatted}</span>
                </Box>
              )
            }}
          />
        )}
      </Box>

      {tableRows.length > 0 && (
        <Box sx={{
          background: '#fff', borderRadius: R, border: '1px solid rgba(0,0,0,0.06)',
          overflow: 'hidden', mt: 2,
        }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: showCosts
              ? '1fr 90px 90px 100px 100px'
              : '1fr 90px 90px',
            px: 2.5, py: 1, borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}>
            {[
              { label: period === 'monthly' ? 'Maand' : 'Datum', align: 'left' },
              { label: 'MCP', align: 'right' },
              { label: 'Workflow', align: 'right' },
              ...(showCosts ? [
                { label: 'MCP kosten', align: 'right' },
                { label: 'Workflow kosten', align: 'right' },
              ] : []),
            ].map((col) => (
              <Typography key={col.label} sx={{
                fontSize: 11, fontWeight: 600, color: 'rgba(0,0,0,0.4)',
                textAlign: col.align as 'left' | 'right',
              }}>
                {col.label}
              </Typography>
            ))}
          </Box>
          {pagedRows.map((row, i) => (
            <Box key={row.label} sx={{
              'display': 'grid',
              'gridTemplateColumns': showCosts
                ? '1fr 90px 90px 100px 100px'
                : '1fr 90px 90px',
              'px': 2.5, 'py': 1.25,
              'borderBottom': i < pagedRows.length - 1 ? '1px solid rgba(0,0,0,0.03)' : 'none',
              'transition': 'background 0.15s',
              '&:hover': { background: 'rgba(0,0,0,0.015)' },
            }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}>
                {row.label}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.mcp, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {formatCompact(row.mcp)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13, fontWeight: 600, color: COLORS.workflow, textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatCompact(row.workflow)}
              </Typography>
              {showCosts && (
                <Typography
                  sx={{
                    fontSize: 13, fontWeight: 600, color: xxllncColor.green100, textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {row.mcpCost > 0 ? formatCurrency(row.mcpCost) : '-'}
                </Typography>
              )}
              {showCosts && (
                <Typography
                  sx={{
                    fontSize: 13, fontWeight: 600, color: xxllncColor.green100, textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {row.workflowCost > 0 ? formatCurrency(row.workflowCost) : '-'}
                </Typography>
              )}
            </Box>
          ))}
          {totalPages > 1 && (
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              px: 2, py: 1, borderTop: '1px solid rgba(0,0,0,0.04)', gap: 1,
            }}>
              <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.4)' }}>
                {tablePage * PAGE_SIZE + 1}–{Math.min((tablePage + 1) * PAGE_SIZE, tableRows.length)} van {tableRows.length}
              </Typography>
              <IconButton size="small" disabled={tablePage === 0} onClick={() => setTablePage(tablePage - 1)}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled={tablePage >= totalPages - 1} onClick={() => setTablePage(tablePage + 1)}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
